<template>
  <div>
    <div v-if="isCustomizeButtonVisible" class="cart-select-p1">
      <pdl-button
        v-if="isFdpAndCustomizationEnabled"
        secondary
        block
        icon="project_one_colorwheel"
        qaid="FDP-customize-button"
        @click="enterCustomizationMode"
      >
        {{ $t('projectOne.button.customize') }}
      </pdl-button>
      <pdl-link
        v-else
        button
        secondary
        block
        icon="project_one_colorwheel"
        qaid="project-one-customize-link"
        :disabled="!isBuyable"
        :href="projectOneConfiguratorUrl"
      >
        {{ $t('projectOne.button.customize') }}
      </pdl-link>
    </div>
    <div v-else-if="isSpectrumEnabled">
      <pdl-button
        qaid="cart-select-spectrum"
        block
        :disabled="hasCustomWaterBottleRestriction"
        icon="build"
        @click="openSpectrumCustomizer"
      >
        {{ $t('product.details.buyingzone.customize') }}
      </pdl-button>
    </div>
    <spectrum-customizer-overlay
      v-if="isSpectrumEnabled"
      ref="spectrumCustomizerOverlay"
      qaid="spectrum-customizer-overlay"
      :has-spectrum-open="hasSpectrumOpen"
      :spectrum-url="spectrumUrl"
      :options="options"
      :custom-product-starting-point="customProductStartingPoint"
      @close-spectrum-customizer="closeSpectrumCustomizer"
    />
  </div>
</template>
<script>
import {Url} from '@/utils/url';
import SpectrumCustomizerOverlay from '@/components/customizer/SpectrumCustomizerOverlay';
import {mapState, mapGetters, mapMutations, mapActions} from 'vuex';
import {PdlButton} from '@pedal/pdl-button';
import {PdlLink} from '@pedal/pdl-link';
import {useFunctionalityToggle} from '@/composables/functionality-toggle.js';
import {Routes} from '@/constants/family-details.js';

export default {
  name: 'CustomizeProductButton',
  components: {
    SpectrumCustomizerOverlay,
    PdlButton,
    PdlLink,
  },
  props: {
    projectOneUrl: {
      type: String,
      default: '',
    },
    spectrumUrl: {
      type: String,
      default: '',
    },
    customProductStartingPoint: {
      type: String,
      default: '',
    },
    customConfiguratorType: {
      type: String,
      default: '',
    },
    siteLocale: {
      type: String,
      default: '',
    },
    isProjectOneConfiguratorActive: {
      type: Boolean,
      default: false,
    },
    isBuyable: {
      type: Boolean,
      default: true,
    },
    options: {
      type: Object,
      default: () => null,
    },
    successMessage: {
      type: String,
      default: '',
    },
    isB2b: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {isToggleActive: isFDPCustomizationEnabled} = useFunctionalityToggle('b2cFDPCustomizationToggle');
    return {isFDPCustomizationEnabled};
  },
  data() {
    return {
      hasSpectrumOpen: false,
    };
  },
  computed: {
    ...mapGetters('checkout', ['isRestrictedToRetailer']),
    ...mapState('productDetails', ['isBikeCustomizable', 'isCustomizationMode']),
    ...mapState('backend', ['hasCustomWaterBottleRestriction']),
    ...mapState('familyDetails', ['models']),
    ...mapState('global', ['headerHeight']),
    ...mapGetters('viewSize', ['viewBreakpointIs']),

    isBikeCustomizationEnabled() {
      return (
        (this.isProjectOneConfiguratorActive || this.isBikeCustomizable) &&
        this.customProductStartingPoint &&
        this.customConfiguratorType === 'ProjectOne'
      );
    },
    isSpectrumEnabled() {
      if (this.spectrumUrl && this.customConfiguratorType === 'SpectrumBottleConfigurator')
        this.setIsSpectrumEnabled(true);
      return !!this.spectrumUrl && this.customConfiguratorType === 'SpectrumBottleConfigurator';
    },
    projectOneConfiguratorUrl() {
      if (this.isBikeCustomizationEnabled) {
        return `${this.siteLocale}/project-one-builder/#/${this.customProductStartingPoint}`;
      }
      return this.projectOneUrl;
    },
    isCustomizeButtonVisible() {
      return this.isBikeCustomizationEnabled && !this.isRestrictedToRetailer && !this.isCustomizationMode;
    },
    isFdpAndCustomizationEnabled() {
      return this.models?.length > 0 && this.isFDPCustomizationEnabled;
    },
  },
  created() {
    this.checkIfSpectrumCustomizerIsPreOpened();
  },
  methods: {
    ...mapMutations('backend', ['setIsSpectrumEnabled']),
    ...mapMutations('productDetails', ['setIsCustomizationMode']),
    ...mapActions('bikeCustomization', ['fetchStartingCustomizationOptions']),

    checkIfSpectrumCustomizerIsPreOpened() {
      const url = new URLSearchParams(document.location.search);
      const hasSpectrumOpen = url.get('hasSpectrumOpen');
      if (hasSpectrumOpen === 'true') {
        this.$nextTick(() => {
          this.removeRedirectParametersFromUrl();
          this.openSpectrumCustomizer();
        });
      }
    },
    removeRedirectParametersFromUrl() {
      window.history.replaceState(
        null,
        document.title,
        Url.update(window.location.href, undefined, {
          removeKeys: ['hasSpectrumOpen'],
        })
      );
    },
    removeRecipeIdFromUrl() {
      window.history.replaceState(
        null,
        document.title,
        Url.update(window.location.href, undefined, {
          removeKeys: ['recipeId'],
        })
      );
    },
    openSpectrumCustomizer() {
      this.hasSpectrumOpen = !this.hasSpectrumOpen;
    },
    closeSpectrumCustomizer(value) {
      this.removeRecipeIdFromUrl();
      this.hasSpectrumOpen = value;
    },
    enterCustomizationMode() {
      this.setIsCustomizationMode(true);
      this.fetchStartingCustomizationOptions(this.customProductStartingPoint);
      this.$router.replace({
        name: Routes.FAMILY_DETAILS_PAGE,
        params: {
          ...this.$route.params,
          sku: null,
        },
      });
    },
  },
};
</script>

<style scoped>
.project-one-icon {
  margin-right: 0.2rem;
}
</style>
